import React, { useState, useEffect } from "react";
import MPActionBar from "./MProjectActionBar/ActionBar";
import DynamicTable from "../../../components/Table/MTable";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import axios from "axios";
import { useSelector } from "react-redux";
import { Loader2 } from "lucide-react";

const MProjects = () => {
  // State Management
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Redux and Environment Variables
  const { role } = useSelector((state) => state.auth);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const apiEndpoint = `${API_BASE_URL}/mproject/mproject`;

  // Table Columns Configuration
  const columns = [
    { key: "ProjectID", label: "Project ID" },
    { key: "ProjectName", label: "Project Name" },
    { key: "LocationName", label: "Location" },
    { key: "ProjectIncharge", label: "Project Incharge" },
    { key: "StageName", label: "Stage" },
    { key: "MailID", label: "Mail ID" }
  ];

  // Fetch Projects Data
  const fetchProjects = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(apiEndpoint);
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      // Ensure unique projects based on ProjectID
      const uniqueProjects = Array.from(
        new Map(response.data.map(item => [item.ProjectID, item])).values()
      );
      
      setProjects(uniqueProjects);
      setFilteredProjects(uniqueProjects);
    } catch (err) {
      console.error("Error fetching projects:", err);
      setError(err.response?.data?.message || "Failed to fetch projects. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch All Projects for Export
  const fetchAllProjects = async () => {
    try {
      const response = await axios.get(`${apiEndpoint}?getAllRecords=true`);
      return response.data || [];
    } catch (err) {
      console.error("Error fetching all projects:", err);
      return [];
    }
  };

  // Search Handler
  const handleSearch = (searchQuery) => {
    if (!searchQuery || searchQuery.trim() === '') {
      setFilteredProjects(projects);
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    
    const filtered = projects.filter(project => {
      const searchableValues = [
        String(project?.ProjectID || ''),
        String(project?.ProjectName || ''),
        String(project?.LocationName || ''),
        String(project?.ProjectIncharge || ''),
        String(project?.StageName || ''),
        String(project?.MailID || '')
      ];

      return searchableValues.some(value => 
        value.toLowerCase().includes(query)
      );
    });

    setFilteredProjects(filtered);
  };

  // Operation Success Handler
  const handleOperationSuccess = React.useCallback(() => {
    fetchProjects();
    setSelectedRow(null);
  }, []);

  // Initial Data Fetch
  useEffect(() => {
    fetchProjects();
    
    // Cleanup function
    return () => {
      setProjects([]);
      setFilteredProjects([]);
      setError(null);
    };
  }, []);

  // Error Component
  if (error) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-100">
        <Header />
        <main className="flex-grow p-6">
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="text-red-600 p-4 rounded-md bg-red-50 border border-red-200">
                <h3 className="text-lg font-semibold mb-2">Error</h3>
                <p>{error}</p>
                <button
                  onClick={() => fetchProjects()}
                  className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Main Component Render
  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      <Header exportEndpoint={apiEndpoint} />

      <div className="flex-grow p-4">
        <div className="max-w-7xl mx-auto">
          {/* Action Bar */}
          <MPActionBar
            title="Project"
            rowKey="ProjectID"
            deleteMessage="Are you sure you want to remove this project permanently?"
            fields={[
              { 
                name: "ProjectName", 
                label: "Project Name", 
                type: "text",
                required: true 
              },
              { 
                name: "LocationID", 
                label: "Location", 
                type: "select", 
                options: "locations",
                required: true 
              },
              { 
                name: "ProjectIncharge", 
                label: "Project Incharge", 
                type: "select", 
                options: "users",
                required: true 
              },
              { 
                name: "StageID", 
                label: "Stage", 
                type: "select", 
                options: "stages",
                required: true 
              },
              { 
                name: "MailID", 
                label: "Mail ID", 
                type: "email",
                required: true 
              },
            ]}
            apiEndpoint={apiEndpoint}
            data={filteredProjects}
            selectedRow={selectedRow}
            onRowSelect={setSelectedRow}
            onRefresh={handleOperationSuccess}
            onSearch={handleSearch}
            userRole="admin"
            fetchAllData={fetchAllProjects}
          />

          {/* Table Section */}
          <div className="mt-6">
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Project List
                </h3>
              </div>

              <div className="p-4">
                {isLoading ? (
                  <div className="flex justify-center items-center h-64">
                    <div className="flex flex-col items-center space-y-2">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                      <p className="text-gray-500">Loading projects...</p>
                    </div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <DynamicTable
                      columns={columns}
                      data={filteredProjects}
                      selectedRow={selectedRow}
                      onRowSelect={setSelectedRow}
                      rowKey="ProjectID"
                      highlightColor="bg-blue-50"
                      noDataMessage="No projects found."
                      className="w-full"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MProjects;